import {defineStore} from 'pinia';
import {type RemovableRef, StorageSerializers, useStorage} from '@vueuse/core';
import {computed, ref} from 'vue';

import ClientAPI from '@/api/ClientAPI';
import psIconDark from '@/assets/images/psIconDark.svg';
import psIconLight from '@/assets/images/psIconLight.svg';
import type {Client} from '@/models/Client';
import clientSubdomain from '@/use/clientSubdomain';
import {isDarkColor, isLightColor} from '@/use/colorUtils';

export const useClientStore = defineStore('client', () => {
  const client: RemovableRef<Client | null> = useStorage('client-branding', null, sessionStorage, {
    serializer: StorageSerializers.object,
  });
  const isLoading = ref(false);
  const menuIsDark = computed(() => {
    const menuBackground = client.value?.sp_menu_background;

    return Boolean(menuBackground && isDarkColor(menuBackground));
  });
  const psIconURL = computed(() => {
    const menuColor = client.value?.sp_menu_color;

    if (!menuColor) {
      return psIconDark;
    }

    return isLightColor(menuColor) ? psIconLight : psIconDark;
  });

  const clear = () => {
    client.value = null;
  };

  const setBranding = (branding: Client) => {
    client.value = branding;
  };

  const load = async (subdomain = clientSubdomain()) => {
    if (!subdomain) {
      clear();
      return;
    }

    isLoading.value = true;
    try {
      client.value = await new ClientAPI().branding(subdomain);
    } finally {
      isLoading.value = false;
    }
  };

  return {
    client,
    isLoading,
    menuIsDark,
    psIconURL,
    clear,
    load,
    setBranding,
  };
});
